import React, { useState, useMemo, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  InputAdornment,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

// Function to dynamically load Bootstrap Icons from the CSS
const loadBootstrapIcons = async () => {
  try {
    // Fetch the Bootstrap Icons CSS file
    const response = await fetch('https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css');
    const cssText = await response.text();
    
    // Extract icon names using regex
    // Matches patterns like .bi-icon-name::before
    const iconPattern = /\.bi-([a-z0-9-]+)::before/g;
    const icons = new Set();
    let match;
    
    while ((match = iconPattern.exec(cssText)) !== null) {
      icons.add(match[1]);
    }
    
    return Array.from(icons).sort();
  } catch (error) {
    console.error('Error loading Bootstrap Icons:', error);
    // Fallback to a basic set if loading fails
    return [
     'alarm', 'archive', 'arrow-left', 'arrow-right', 'bag', 'bell', 'book',
    'bookmark', 'box', 'briefcase', 'building', 'calendar', 'camera', 'cart',
    'chat', 'check', 'clipboard', 'clock', 'cloud', 'code', 'cup', 'download',
    'envelope', 'eye', 'file', 'flag', 'folder', 'gear', 'gift', 'globe',
    'graph-up', 'grid', 'hand-thumbs-up', 'heart', 'house', 'image', 'inbox',
    'info-circle', 'journal', 'key', 'laptop', 'lightbulb', 'list', 'lock',
    'map', 'megaphone', 'mic', 'moon', 'music-note', 'palette', 'paperclip',
    'pencil', 'people', 'person', 'phone', 'pin-map', 'play', 'plus', 'printer',
    'puzzle', 'question-circle', 'receipt', 'search', 'send', 'share', 'shield',
    'star', 'sun', 'tag', 'trash', 'trophy', 'truck', 'umbrella', 'upload',
    'wallet', 'wrench', 'x-circle', 'zoom-in', 'activity', 'airplane', 'asterisk',
    'award', 'bar-chart', 'battery', 'broadcast', 'bug', 'calculator', 'cash',
    'clipboard-check', 'cloud-download', 'collection', 'cone', 'credit-card',
    'cursor', 'database', 'diagram', 'document', 'droplet', 'emoji-smile',
    'filter', 'fire', 'fingerprint', 'flower', 'gem', 'hammer', 'handbag',
    'headphones', 'hospital', 'hourglass', 'journal-text', 'layers', 'lightning',
    'link', 'mailbox', 'menu-button', 'minecart', 'newspaper', 'outlet',
    'patch-check', 'pie-chart', 'piggy-bank', 'power', 'qr-code', 'rocket',
    'safe', 'scissors', 'shop', 'shuffle', 'signpost', 'sliders', 'speedometer',
    'stopwatch', 'tablet', 'terminal', 'thermometer', 'ticket', 'toggles',
    'tools', 'tree', 'tv', 'usb', 'vector-pen', 'volume-up', 'watch', 'wifi'
    ];
  }
};

const IconPickerField = ({ value, onChange, label = "Choose Icon" }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [bootstrapIcons, setBootstrapIcons] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load icons when dialog opens for the first time
  useEffect(() => {
    if (open && bootstrapIcons.length === 0) {
      setLoading(true);
      loadBootstrapIcons().then(icons => {
        setBootstrapIcons(icons);
        setLoading(false);
      });
    }
  }, [open, bootstrapIcons.length]);
useEffect(() => {
  // Inject Bootstrap Icons CSS once
  injectBootstrapIconsCSS();
}, []);
  const filteredIcons = useMemo(() => {
    if (!searchQuery.trim()) return bootstrapIcons;
    return bootstrapIcons.filter(icon =>
      icon.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, bootstrapIcons]);

  const handleIconSelect = (icon) => {
    console.log("Handle ICon Select");
    onChange(icon);
    setOpen(false);
    setSearchQuery('');
  };

  const handleClearIcon = () => {
    onChange('');
  };

  const handleClose = () => {
    setOpen(false);
    setSearchQuery('');
  };
// Function to inject Bootstrap Icons CSS into <head>
const injectBootstrapIconsCSS = () => {
  if (!document.querySelector('#bootstrap-icons-css')) {
    const link = document.createElement('link');
    link.id = 'bootstrap-icons-css';
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css';
    document.head.appendChild(link);
  }
};

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setOpen(true)}
          sx={{
            justifyContent: 'space-between',
            textTransform: 'none',
            height: 40
          }}
        >
          <Box  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {value ? (
              <>
                <i className={`bi bi-${value}`} style={{ fontSize: '20px' }}></i>
                <Typography variant="body2">{value}</Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
            )}
          </Box>
          <SearchIcon fontSize="small" />
        </Button>
        {value && (
          <IconButton size="small" onClick={handleClearIcon} color="error">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Select an Icon</Typography>
              {bootstrapIcons.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {filteredIcons.length} of {bootstrapIcons.length} icons
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search icons..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            disabled={loading}
          />
          <Paper
            variant="outlined"
            sx={{
              maxHeight: 400,
              overflow: 'auto',
              p: 2
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={1}>
                {filteredIcons.length > 0 ? (
                  filteredIcons.map((icon) => (
                    <Grid size={{xs:3,sm:2}} key={icon}>
                      <Tooltip title={icon} arrow>
                        <Paper
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                            transition: 'all 0.2s',
                            border: value === icon ? '2px solid' : '1px solid',
                            borderColor: value === icon ? 'primary.main' : 'divider',
                            bgcolor: value === icon ? 'primary.light' : 'background.paper',
                            '&:hover': {
                              bgcolor: 'action.hover',
                              transform: 'scale(1.05)',
                              boxShadow: 2
                            }
                          }}
                          onClick={() => handleIconSelect(icon)}
                        >
                          <i className={`bi bi-${icon}`} style={{ fontSize: '24px' }}></i>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '9px',
                              textAlign: 'center',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%'
                            }}
                          >
                            {icon}
                          </Typography>
                        </Paper>
                      </Tooltip>
                    </Grid>
                  ))
                ) : (
                  <Grid size={{xs:12}} >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ py: 4 }}
                    >
                      {searchQuery ? `No icons found matching "${searchQuery}"` : 'No icons available'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IconPickerField;

// INTEGRATION EXAMPLE:
// Add this to your survey form, below the End Date field:
/*
<Grid size={{xs:12}}>
  <IconPickerField
    value={survey.icon || ''}
    onChange={(icon) => handleInputChange('icon')({ target: { value: icon } })}
    label="Choose Survey Icon"
  />
</Grid>
*/

// Don't forget to add Bootstrap Icons CSS to your HTML:
// <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css">